import type {
  GitPendingProgressReviews,
  GitProgressBranchList,
  GitProgressReviewWarnings,
  GitProgressCommitList,
  GitProgressCommitsStatus,
  ReportGitProgressCommitsInput,
  TaskGitProgressReports,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/**
 * Gắn cập nhật tiến độ với commit — APM-PCOM, docs/plan-progress-commits.md §3.
 * Tệp riêng (không nối vào `api.ts`) như `git-progress-commits.ts` ở contracts.
 */
const base = (projectId: string): string => `/projects/${projectId}/git/progress-commits`;

export const progressCommitsApi = {
  status: (projectId: string) => apiFetch<GitProgressCommitsStatus>(`${base(projectId)}/status`),

  /** `reviewWithAi` vắng = giữ nguyên công tắc con (APM-PCRV). */
  setRequired: (projectId: string, enabled: boolean, reviewWithAi?: boolean) =>
    apiFetch<GitProgressCommitsStatus>(base(projectId), {
      method: 'PUT',
      body: reviewWithAi === undefined ? { enabled } : { enabled, reviewWithAi },
    }),

  branches: (projectId: string, taskId: string) =>
    apiFetch<GitProgressBranchList>(
      `${base(projectId)}/branches?taskId=${encodeURIComponent(taskId)}`,
    ),

  /** Gọi máy chủ git — ≤ 100 commit mới nhất của nhánh. */
  commits: (projectId: string, repositoryId: string, branch: string, all: boolean) =>
    apiFetch<GitProgressCommitList>(
      `${base(projectId)}/commits?${new URLSearchParams({
        repositoryId,
        branch,
        ...(all ? { all: '1' } : {}),
      }).toString()}`,
    ),

  reports: (projectId: string, taskId: string) =>
    apiFetch<TaskGitProgressReports>(`${base(projectId)}/tasks/${taskId}`),

  report: (projectId: string, taskId: string, body: ReportGitProgressCommitsInput) =>
    apiFetch<TaskGitProgressReports>(`${base(projectId)}/tasks/${taskId}`, {
      method: 'POST',
      body,
    }),

  remove: (
    projectId: string,
    taskId: string,
    reportId: string,
    repositoryId: string,
    sha: string,
  ) =>
    apiFetch<TaskGitProgressReports>(
      `${base(projectId)}/tasks/${taskId}/reports/${reportId}/commits/${repositoryId}/${sha}`,
      { method: 'DELETE' },
    ),

  // ── AI rà commit — APM-PCRV ───────────────────────────────────────────────

  /** Việc có lượt rà thấp chưa "Đã xem" — một lần cho cả bảng. */
  warnings: (projectId: string) =>
    apiFetch<GitProgressReviewWarnings>(`${base(projectId)}/reviews/warnings`),

  acknowledge: (projectId: string, taskId: string, reportId: string) =>
    apiFetch<TaskGitProgressReports>(
      `${base(projectId)}/tasks/${taskId}/reviews/${reportId}/acknowledge`,
      { method: 'POST' },
    ),

  retry: (projectId: string, taskId: string, reportId: string) =>
    apiFetch<TaskGitProgressReports>(
      `${base(projectId)}/tasks/${taskId}/reviews/${reportId}/retry`,
      { method: 'POST' },
    ),

  /** Lượt rà của chính mình đang chạy ngầm + vừa xong — chữ (i) ở thanh trên. */
  pendingReviews: () => apiFetch<GitPendingProgressReviews>('/git/progress-reviews/pending'),
};

export const progressCommitsKeys = {
  status: (projectId: string) => ['git', projectId, 'progress-commits', 'status'] as const,
  branches: (projectId: string, taskId: string) =>
    ['git', projectId, 'progress-commits', 'branches', taskId] as const,
  commits: (projectId: string, repositoryId: string, branch: string, all: boolean) =>
    ['git', projectId, 'progress-commits', 'commits', repositoryId, branch, all] as const,
  reports: (projectId: string, taskId: string) =>
    ['git', projectId, 'progress-commits', 'reports', taskId] as const,
  warnings: (projectId: string) => ['git', projectId, 'progress-commits', 'warnings'] as const,
  /** Cấp người dùng, không theo dự án. */
  pendingReviews: () => ['git', 'progress-reviews', 'pending'] as const,
};
